import React from 'react';
import classNames from 'classnames';
import { css } from '@emotion/react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { ButtonProps } from './Button';
import { ButtonIcon } from './ButtonIcon';
import type { TextButtonSize, TextButtonVariant } from './types';
import {
  button_base_css,
  text_button_size_css,
  text_button_text_css,
  getTextButtonStyle,
  text_button_icon_size_css,
} from './styles';
import { semantic_colors } from '../../foundation';
import { IconLoadingDonuts } from '../icons/generated';

export type { TextButtonVariant } from './types';

export interface TextButtonProps extends Omit<ButtonProps, 'loading' | 'variant' | 'color' | 'size'> {
  /** @default 'text' */
  variant?: TextButtonVariant;
  /** @default false */
  loading?: boolean;
  /** @default false */
  underline?: boolean;
  /** @default 'medium' */
  size?: TextButtonSize;
  /**
   * @default
   * variant='text'
   * semantic_colors.content.primary
   * variant='link'
   * semantic_colors.accent.primary
   */
  color?: string;
}

export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      type = 'button',
      variant = 'text',
      underline = false,
      size = 'medium',
      color,
      fill,
      loading,
      disabled,
      leftIcon,
      rightIcon,
      className,
      children,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const isTextVariant = variant === 'text';
    const isDisabled = loading || disabled;
    const text_variant_color = color ?? semantic_colors.content.primary;
    const link_variant_color = color ?? semantic_colors.accent.primary;
    const fixed_color = isTextVariant ? text_variant_color : link_variant_color;
    const icon_props = {
      size: text_button_icon_size_css[size],
      ...(!isTextVariant && {
        color: 'currentColor',
      }),
    };
    const loading_indicator = loading && (
      <ButtonIcon {...icon_props}>
        <IconLoadingDonuts size={30} />
      </ButtonIcon>
    );
    const left_icon_node = leftIcon && (
      <ButtonIcon {...icon_props}>{loading ? <IconLoadingDonuts size={30} /> : leftIcon}</ButtonIcon>
    );
    const right_icon_node = rightIcon && (
      <ButtonIcon {...icon_props}>{loading && !leftIcon ? <IconLoadingDonuts size={30} /> : rightIcon}</ButtonIcon>
    );
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        type={type}
        className={classNames('pds-textbutton', className)}
        css={[
          button_base_css,
          text_button_text_css[size],
          isTextVariant && text_button_size_css[size],
          getTextButtonStyle(variant, fixed_color),
          css`
            ${fill && 'width: 100%;'};
            ${underline && 'text-decoration: underline'};
            cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
            border: none;
          `,
        ]}
        disabled={isDisabled}
        {...props}
        ref={ref}
      >
        {left_icon_node || (!right_icon_node && loading_indicator)}
        <Slottable>{children}</Slottable>
        {right_icon_node}
      </Comp>
    );
  },
);
